// @theme component
import React, { useCallback, useState, useEffect, useRef } from 'react';
import style from '@theme/js/components/main/SmsVerification/index.scss';
import { STEPS_NAME, VERIFICATION_TYPES, PASSCODE_LENGTH, ERROR_SERVICE_UNAVAILABLE, ERROR_SERIVCE_LIMITS_REACHED, ERROR_INTERVAL_REACHED } from '@theme/js/components/main/SmsVerification/constants';
import { SmsVerificationStepType } from '@theme/js/components/main/SmsVerification';
import { useTranslations } from '@core/js/hooks/i18n';
import { useUserSelector } from '@core/js/store/hooks';
import { prop } from 'ramda';
import { sendPhoneVerificationCode, startPhoneVerification } from '@core/js/components/main/PhoneVerification/actions';
import { Link } from '@core/js/components/common/Link/Link';
import { dispatcher } from 'dispatcher';
import Form from '@core/js/components/common/Form';
import { clsx } from '@core/js/utils/classNames.util';
import { PasscodeInput } from '@theme/js/components/main/SmsVerification/components/PasscodeInput/PasscodeInput';
import { useUpdatePlayerInfo } from '@core/js/hooks/socket';
import { useFetchUserDetails } from '@core/js/hooks/user';
import ErrorMessage from '@theme/js/components/main/SmsVerification/components/ErrorMessage/ErrorMessage';

type FormRenderProps = {
    handleSubmit: () => void
}

const PasscodeStep = React.memo(({ setStep, type }: SmsVerificationStepType) => {
    const [passcode, setPasscode] = useState('');
    const { cellphone } = useUserSelector(prop('userDetails'));
    const t = useTranslations('playtech.mobile.system.verification.');
    const iconCls = type === VERIFICATION_TYPES.DEFAULT ? style.mobileIcon : style.giftIcon;
    const [passcodeError, setPasscodeError] = useState('');
    const [codeResendError, setCodeResendError] = useState('');
    const codeResendErrorDuration = useRef<number>();
    const updatePlayerInfo = useUpdatePlayerInfo();
    const fetchUserDetails = useFetchUserDetails();

    const handleUserBlock = useCallback((requestError?: any, interval: number = 0) => {
        const errorMessage = decodeURIComponent(requestError?.response?.data?.userErrors?.[0]?.message || '');

        if (!errorMessage) {
            codeResendErrorDuration.current = Date.now() + interval * 1000;
            setCodeResendError(t('error.interval'));
            return;
        }

        const [message, time] = errorMessage?.split('@') || [];
        const [hours, minutes, seconds] = time?.split(':') || [];

        const blockedTill = new Date();
        blockedTill.setHours(blockedTill.getHours() + Number(hours));
        blockedTill.setMinutes(blockedTill.getMinutes() + Number(minutes));
        blockedTill.setSeconds(blockedTill.getSeconds() + Number(seconds));
        codeResendErrorDuration.current = blockedTill.getTime();
        setCodeResendError(message);
    }, [t]);

    const handleError = useCallback((requestError: any, interval?: number) => {
        const errorCode = requestError?.response?.data?.errorCode || ERROR_INTERVAL_REACHED;

        switch (errorCode) {
            case ERROR_SERVICE_UNAVAILABLE:
                setStep?.(STEPS_NAME.UNAVAILABLE);
                break;
            case ERROR_SERIVCE_LIMITS_REACHED:
                handleUserBlock(requestError);
                break;
            case ERROR_INTERVAL_REACHED:
                handleUserBlock(null, interval);
                break;
            default:
                break;
        }
    }, [setStep, handleUserBlock]);

    const sendSMS = useCallback(async () => {
        let success = false;

        try {
            const result = await startPhoneVerification();
            const interval = result?.data?.interval;

            if (interval) {
                handleError(null, interval);
            }

            if (!result.data.errorCode) {
                success = true;
            }
        } catch (e) {
            handleError(e);
        }

        dispatcher.dispatch('auth:phone:verificationInit', { event: 'PhoneVerificationInit', success, variant: type });
    }, [type, handleError]);

    const handleSubmitClick = useCallback(async (forcePasscode?: string) => {
        let status;

        try {
            const result = await sendPhoneVerificationCode({ code: forcePasscode || passcode });
            status = result?.data?.status;
        } catch (e) {
            handleError(e);
        }

        if (status === 'success') {
            setStep?.(STEPS_NAME.SUCCESS);
            await updatePlayerInfo({ dataMap: { phone: cellphone } });
            await fetchUserDetails();
        } else if (status === 'failure') {
            setPasscodeError('passcode.wrong');
        }

        dispatcher.dispatch('auth:phone:verificationComplete', {
            event: 'PhoneVerificationComplete',
            success: status === 'success',
            variant: type
        });
    }, [passcode, setStep, type, cellphone, handleError, fetchUserDetails, updatePlayerInfo]);

    useEffect(() => {
        sendSMS();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        setPasscodeError('');
    }, [passcode]);

    return (
        <Form onSubmit={() => handleSubmitClick()}
            render={({ handleSubmit }: FormRenderProps) => (
            <form onSubmit={handleSubmit}>
                <div className={clsx([style.icon, iconCls])}></div>
                {type === VERIFICATION_TYPES.INCENT && (
                    <div className={style.message}>{t('passcode.message')}</div>
                )}
                <div className={style.description}>{t('passcode.description')}</div>
                <div className={style.phoneNumber}>{cellphone}</div>
                <Link className={style.link} onClick={() => setStep?.(STEPS_NAME.CHANGE_NUMBER)}>
                    {t('changePhone')}
                </Link>
                <div className={style.passwordInputLabel}>{t('passcode.passwordLabel')} </div>
                <PasscodeInput
                    passcode={passcode}
                    setPasscode={setPasscode}
                    handleSubmit={handleSubmitClick}
                />
                {!codeResendError && <Link className={style.link} onClick={sendSMS}>
                    {t('passcode.sendAgain')}
                </Link>}
                <ErrorMessage
                    error={codeResendError}
                    useTKey={false}
                    timerTillMs={codeResendErrorDuration.current}
                    timerEndCallback={() => setCodeResendError('')}
                />
                <ErrorMessage error={passcodeError} />
                <button type="submit" disabled={passcode.length !== PASSCODE_LENGTH} className={style.verificationButton}>
                    {t('passcode.submit')}
                </button>
            </form>
        )}/>
    );
});

export default PasscodeStep;
